import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslation } from 'react-i18next';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import { authService } from '@/services/auth.service';
import { getCurrentDomain } from '@/utils/domain';
import { useAuthPageTheme } from '@/hooks/useAuthPageTheme';
import { resolvePasswordValidationMessage } from '@/lib/auth/password-validation';
import { formatPasswordPolicyRules } from '@/lib/auth/password-policy';
import {
  extractErrorStatus,
  extractJsonApiErrorCode,
  extractJsonApiErrorMessage,
  extractJsonApiFieldErrors,
} from '@/lib/jsonapi/errors';

type FormValues = {
  email: string;
  password: string;
  passwordConfirmation: string;
};

export default function CustomerResetPassword() {
  useAuthPageTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('customer.dashboard.providerFallback');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const emailFromQuery = params.get('email') ?? '';
  const token = params.get('token') ?? '';
  const scope = params.get('scope');

  useEffect(() => {
    if (scope === 'provider') {
      navigate(`/provider/reset-password${location.search}`, { replace: true });
    }
  }, [location.search, navigate, scope]);

  const domainInfo = useMemo(() => getCurrentDomain(), []);
  const resolvedTenantSlug = provider?.slug ?? domainInfo.subdomain ?? undefined;
  const { enabled: passwordLoginEnabled, isLoading: isAvailabilityLoading, policy } =
    usePasswordLoginAvailability('customer', {
      tenantSlug: resolvedTenantSlug,
    });
  const passwordRules = useMemo(() => formatPasswordPolicyRules(t, policy), [policy, t]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const schema = useMemo(
    () =>
      z
        .object({
          email: z.string().email(t('customer.portal.login.invalidEmail')),
          password: z.string().min(8, t('auth.passwordTooShort')),
          passwordConfirmation: z.string(),
        })
        .refine((values) => values.password === values.passwordConfirmation, {
          message: t('customer.portal.resetPassword.passwordMismatch'),
          path: ['passwordConfirmation'],
        }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: emailFromQuery,
      password: '',
      passwordConfirmation: '',
    },
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      authService.resetCustomerPassword({
        email: values.email,
        token,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      }),
    onSuccess: (response) => {
      setErrorMessage(null);
      const message = response.message || t('customer.portal.resetPassword.success');
      toast.success(message);
      navigate('/customer/login', { replace: true });
      form.clearErrors();
    },
    onError: (error: unknown) => {
      const status = extractErrorStatus(error);
      const code = extractJsonApiErrorCode(error);
      const fieldErrors = extractJsonApiFieldErrors(error);

      Object.entries(fieldErrors).forEach(([field, message]) => {
        const resolvedMessage = resolvePasswordValidationMessage(t, message) ?? message;
        if (field === 'email') {
          form.setError('email', { message: resolvedMessage });
        }
        if (field === 'password') {
          form.setError('password', { message: resolvedMessage });
        }
        if (field === 'passwordConfirmation') {
          form.setError('passwordConfirmation', { message: resolvedMessage });
        }
      });

      const rawMessage = extractJsonApiErrorMessage(error, t('customer.portal.resetPassword.error'));
      let message = resolvePasswordValidationMessage(t, rawMessage) ?? rawMessage;
      if (code === 'PASSWORD_RESET_INVALID') {
        message = t('customer.portal.resetPassword.invalidToken');
      }
      if (status === 423 || code === 'PASSWORD_LOGIN_DISABLED') {
        message = t('customer.portal.login.passwordLoginDisabled');
      }
      setErrorMessage(message);
    },
  });

  const tokenMissing = token.trim().length === 0;

  const handleSubmit = (values: FormValues) => {
    setErrorMessage(null);

    if (tokenMissing) {
      setErrorMessage(t('customer.portal.resetPassword.invalidLink'));
      return;
    }

    mutation.mutate(values);
  };

  const isFormDisabled =
    mutation.isPending || isAvailabilityLoading || !passwordLoginEnabled || tokenMissing;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="mx-auto h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="mx-auto text-2xl font-semibold tracking-tight text-foreground">
              {brandName}
            </span>
          )}
          <CardTitle>{t('customer.portal.resetPassword.title')}</CardTitle>
          <CardDescription>{t('customer.portal.resetPassword.description')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {!passwordLoginEnabled && !isAvailabilityLoading ? (
            <Alert variant="destructive">
              <AlertDescription>{t('customer.portal.login.passwordLoginDisabled')}</AlertDescription>
            </Alert>
          ) : null}

          {tokenMissing ? (
            <Alert variant="destructive">
              <AlertDescription>{t('customer.portal.resetPassword.invalidLink')}</AlertDescription>
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.portal.login.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        placeholder={t('customer.portal.login.emailPlaceholder')}
                        disabled={isFormDisabled}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.portal.resetPassword.passwordLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder={t('customer.portal.resetPassword.passwordPlaceholder')}
                          disabled={isFormDisabled}
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((previous) => !previous)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                          aria-label={t(
                            showPassword
                              ? 'customer.portal.resetPassword.hidePassword'
                              : 'customer.portal.resetPassword.showPassword',
                            showPassword ? 'Hide password' : 'Show password'
                          )}
                          title={t(
                            showPassword
                              ? 'customer.portal.resetPassword.hidePassword'
                              : 'customer.portal.resetPassword.showPassword',
                            showPassword ? 'Hide password' : 'Show password'
                          )}
                          disabled={isFormDisabled}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passwordConfirmation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.portal.resetPassword.confirmPasswordLabel')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder={t('customer.portal.resetPassword.confirmPasswordPlaceholder')}
                          disabled={isFormDisabled}
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((previous) => !previous)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                          aria-label={t(
                            showConfirmPassword
                              ? 'customer.portal.resetPassword.hidePassword'
                              : 'customer.portal.resetPassword.showPassword',
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          )}
                          title={t(
                            showConfirmPassword
                              ? 'customer.portal.resetPassword.hidePassword'
                              : 'customer.portal.resetPassword.showPassword',
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          )}
                          disabled={isFormDisabled}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {passwordRules.length > 0 ? (
                <div className="rounded-md border border-muted bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
                  <p className="mb-1 text-foreground">
                    {t('auth.passwordPolicy.title', 'Password requirements')}
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    {passwordRules.map((rule) => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <Button type="submit" className="w-full" disabled={isFormDisabled}>
                {mutation.isPending
                  ? t('customer.portal.resetPassword.submitting')
                  : t('customer.portal.resetPassword.submit')}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => navigate('/customer/login')}
          >
            {t('customer.portal.resetPassword.backToLogin')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
